import type { Logger } from "@expert/logging";

import { HTTPError, type KyInstance } from "ky";

import type { SalesAnalyticsDispatcher } from "../sales-context/SalesContext.types";
import type { SalesProvider } from "../types/types-sales";

import {
    MAX_NOTIFICATION_ATTEMPTS_REACHED_CODE,
    MaxNotificationAttemptsError,
    type SendNotificationErrorResponse,
} from "./sendNotification.errors";

export type SendNotificationPayload = {
    sessionId: string;
    partner: string;
    productOfferId: string;
    productSku: string;
    customerEmailId?: string;
    customerMdn?: string;
    customerChatId?: string;
    workerSid?: string;
    topic?: string;
    language?: string;
    guidedBrowsing?: boolean;
    /** The provider eligibility stamped on the offer; the backend routes the link to it. */
    salesProvider?: SalesProvider;
};

export type SendNotificationResponse = {
    maxAttemptsReached: boolean;
    currentAttempt: number;
    remainingAttempts: number;
    url?: string;
    /** Present only for guided browsing phase two: `url` with a live consent-relay session attached. */
    sessionUrl?: string;
};

export async function sendNotification(
    client: KyInstance,
    logger: Logger,
    dispatcher: SalesAnalyticsDispatcher,
    payload: SendNotificationPayload,
): Promise<SendNotificationResponse> {
    if (!payload.customerEmailId && !payload.customerMdn) {
        throw new Error("Either customerEmailId or customerMdn must be provided");
    }
    const childLogger = logger.child({ module: "sendNotification" });

    childLogger.info({ payload }, "Sending notification");

    try {
        // retry:0 to match the siblings in this folder. ky's default retry.methods excludes POST, so
        // this is belt-and-braces today — but the endpoint enforces a per-session send limit that only
        // clears on a new session, so an automatic retry could never succeed and would burn attempts.
        const response = await client.post("v1/send-notification", { json: payload, retry: 0 });
        // NOTE: effectively dead with the real client. ky's `throwHttpErrors` defaults to true, so a
        // 4xx/5xx throws `HTTPError` from `.post(...)` above and lands in the catch — this `!response.ok`
        // branch is never reached unless a caller passes a client built with `throwHttpErrors: false`.
        // Proven by sendNotification.integration.test.ts ("...ky throws HTTPError before the response.ok
        // check is reached"). Left in place deliberately; remove only alongside that test.
        if (!response.ok) {
            throw new Error(`Failed to send notification: ${response.status} ${response.statusText}`);
        }
        const data = await response.json<SendNotificationResponse>();
        if (data === null) {
            throw new Error("Received null response from notification service");
        }
        void dispatcher.dispatchBusinessEvent("NotificationSent");
        return data;
    } catch (error) {
        // A 403 carrying `code: MAX_NOTIFICATION_ATTEMPTS_REACHED` is the per-session attempt limit
        // refusing a further send — no delivery was attempted, so it is not a delivery failure.
        // Counting it as one inflates the numerator of the SmartLink delivery-failure rate
        // (NotificationSendFailure / (failure + NotificationSent)) with requests that never reached
        // a provider. Classify it into a typed error here: this catch is the only place that holds
        // the ky Response, whose body can be read exactly once, so consumers cannot re-parse it.
        // Matches the InvalidMdnError precedent in eligibility.ts.
        //
        // Deliberately keyed on the body `code`, not the bare 403 status. An authorizer or gateway
        // 403 is a real failure and must stay in the KPI — and must not tell the expert they hit a
        // send limit they never hit.
        if (error instanceof HTTPError && error.response.status === 403) {
            let body: SendNotificationErrorResponse | undefined;
            try {
                body = await error.response.json<SendNotificationErrorResponse>();
            } catch {
                childLogger.warn({ error }, "send-notification 403 body was not valid JSON");
            }
            if (body?.code === MAX_NOTIFICATION_ATTEMPTS_REACHED_CODE) {
                childLogger.warn({ code: body.code }, "send-notification refused: attempt limit reached");
                throw new MaxNotificationAttemptsError(body.message, { cause: error });
            }
        }
        void dispatcher.dispatchBusinessEvent("NotificationSendFailure");
        childLogger.warn({ error }, "Error sending notification");
        throw error;
    }
}
